import React, { useState } from 'react'
import { CancelBtn } from 'components/Button/CancelBtn'
import { Input } from '../../../components/Input/Input'
import { NavBar } from '../../../components/NavBar/NavBar'
import {
    Animation,
    Box,
    Button,
    ButtonsContainer,
    Flex,
    H1WithBack,
    ItemsGrid,
    PageFooter,
    PageWrapper,
    Separator,
    SquareIcon,
    ThinSeparator,
} from '../../../styling/GlobalStyles'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { ISelectValue } from 'constants/globalTypes'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { IAddInventoryRequestState } from 'types/inventory.interface'
import { addInventoryRequest, notifyUserForInventoryRequest } from 'features/inventory/inventoryItemsSlice'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { parseLocationRegistryOptions } from 'features/locationRegistry/locationRegistryMappers'
import { BodyText, TitleText } from 'styling/Typography'
import { PlusIcon } from 'assets/icons/PlusIcon'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'

export const AddInventoryRequest = () => {
    const [state, setState] = useState<IAddInventoryRequestState>({
        type: null,
        location: null,
        assigneeUser: null,
        notes: '',
        lines: [{
            id: new Date().getMilliseconds(),
            item: null,
            quantity: null,
            serials: []
        }
        ]
    })
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()


    const handleSubmit = async (e: React.MouseEvent) => {
        e.preventDefault()
        if (!state.type?.value || !state.assigneeUser?.value || state.lines.some(l => l.item.serial && l.serials.some(s => !s.value)) || state.lines.some(l => !l.item?.value || !l.quantity)) {
            toast.warn('All fields must be valid')
            return
        }
        const resp = await dispatch(addInventoryRequest(state))
        if (resp.meta.requestStatus === 'fulfilled') {
            dispatch(
                setConfirmModal({
                    isOpen: true,
                    onSave: async () => {
                        await dispatch(notifyUserForInventoryRequest(resp.payload.id))
                        navigate('/inventory/requests')
                    },
                    setParams: {
                        type: ModalTypes.BeforeSabe,
                    },
                    onCancel: () => {
                        navigate('/inventory/requests')
                    },
                    cancelText: 'No',
                    confirmText: 'Yes',
                    sentence: ' Do you want to notify employees in charge about this inventory request? ',
                }),
            )

        }
    }


    useFocusFirstInput()

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setState((prev) => {
            const copy = { ...prev }
            copy[name] = value
            return copy
        })
    }

    const handleSelect = (value: ISelectValue, name: string) => {
        setState((prev) => {
            const copy = { ...prev }
            copy[name] = value
            return copy
        })
    }

    const options = [
        { label: 'Issue', value: 1 },
        { label: 'Return', value: 2 },
    ]

    const handleLineSelect = (value: ISelectValue, name: string, id: number) => {
        const copy = structuredClone(state)
        const lineIndex = copy.lines.findIndex(l => l.id === id)
        if (lineIndex > -1) {
            copy.lines[lineIndex][name] = value
            setState(copy)
        }
    }

    const handleLineQuantity = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
        const { valueAsNumber } = e.target
        if (valueAsNumber > 20) {
            toast.warn('Max quantity is 20')
        } else {
            const copy = structuredClone(state)
            const lineIndex = copy.lines.findIndex(l => l.id === id)
            if (lineIndex > -1) {
                copy.lines[lineIndex]['quantity'] = valueAsNumber
                // adjust serials
                const currentSerials = copy.lines[lineIndex]['serials']
                if (currentSerials.length < valueAsNumber) {
                    for (let i = currentSerials.length; i < valueAsNumber; i++) {
                        currentSerials.push({ id: new Date().getMilliseconds() + i, label: '', value: null })
                    }
                } else if (currentSerials.length > valueAsNumber) {
                    copy.lines[lineIndex]['serials'] = currentSerials.slice(0, valueAsNumber)
                }

                setState(copy)
            }
        }
    }

    const handleSerial = (value: ISelectValue, name: string, lineId: number, serialId: number) => {
        const copy = structuredClone(state)
        const lineIndex = copy.lines.findIndex(l => l.id === lineId)
        if (lineIndex > -1) {
            const serialIndex = copy.lines[lineIndex].serials.findIndex(s => s.id === serialId)
            if (serialIndex > -1) {
                copy.lines[lineIndex].serials[serialIndex] = { ...copy.lines[lineIndex].serials[serialIndex], ...value }
                setState(copy)
            }
        }
    }

    const handleAddLine = () => {
        setState(prev => ({
            ...prev, lines: [...prev.lines, {
                id: new Date().getMilliseconds(),
                item: null,
                quantity: null,
                serials: []
            }]
        }))
    }

    return (
        <>
            <NavBar />
            <Animation>
                <PageWrapper>
                    <H1WithBack arrow title='Add Inventory Request' />
                    <Separator />
                    <ItemsGrid columns={3}>
                        <HonestSelectInput
                            options={options}
                            labelText='Type'
                            name='type'
                            onChange={handleSelect}
                            invalid={!state.type?.value}
                            required
                        />
                        <HonestSelectAsyncInput
                            nomarg
                            link='locations'
                            placeholder='Search locations'
                            name='location'
                            labelText='Location'
                            defaultValue={state?.location?.label}
                            onChange={handleSelect}
                            parserFunction={parseLocationRegistryOptions}
                            required
                            invalid={!state.location?.value}
                        />
                        <HonestSelectAsyncInput
                            link='users/basic'
                            labelText='Assignee User'
                            name='assigneeUser'
                            onChange={handleSelect}
                            parserFunction={(data: any) => data.data.map((i: any) => ({ label: i.fullName, value: i.id }))}
                            defaultValue={state.assigneeUser?.label}
                            invalid={!state.assigneeUser?.value}
                            required
                        />
                    </ItemsGrid>
                    <Input
                        name='notes'
                        labelText='Notes'
                        placeholder='Enter notes'
                        value={state.notes}
                        onChange={handleChange}
                        type='textarea'
                        height='100px'
                    />
                    <ThinSeparator />
                    <Flex mb='16' width='20%' between  >
                        <TitleText weight='bold' >Items</TitleText>
                        <SquareIcon onClick={handleAddLine} >
                            {PlusIcon}
                        </SquareIcon>
                    </Flex>
                    {
                        state.lines.length === 0 ? <BodyText   >No items added yet.</BodyText> : state.lines.map((l, i) => <Box key={l.id} >
                            <Flex column >
                                <BodyText weight='bold' >Item {i + 1}</BodyText>
                                <ItemsGrid mt='1rem' columns={4} >

                                    <HonestSelectAsyncInput
                                        name='item'
                                        labelText='Item'
                                        onChange={(v, n) => handleLineSelect(v, n, l.id)}
                                        link='inventory/item/stock' // TODO ITEM
                                        additionalParams={{ locationId: state.location?.value }}
                                        defaultValue={l.item?.label}
                                        parserFunction={(data: any) => data.data.map((i: any) => ({
                                            label: i.name, value: i.id, serial: i.trackingMode == 4,
                                        }))}
                                        invalid={!l.item?.value}
                                        required
                                    />
                                    <Input
                                        name='quantity'
                                        labelText='Quantity'
                                        type='number'
                                        placeholder='Enter quantity'
                                        value={l.quantity}
                                        onChange={(e) => handleLineQuantity(e, l.id)}
                                        required
                                    />
                                    {l.item?.serial && !!l.serials.length && <>
                                        {
                                            l.serials.map((s, i) =>
                                                <HonestSelectAsyncInput
                                                    key={i}
                                                    name='serial'
                                                    labelText={`Serial ${i + 1}`}
                                                    onChange={(v, n) => handleSerial(v, n, l.id, s.id)}
                                                    link={`inventory/item/${l.item?.value}/serial`}
                                                    defaultValue={s.label}
                                                    parserFunction={(data: any) => data.data.map((i: any) => ({
                                                        label: i.serialNo, value: i.id
                                                    }))}
                                                    invalid={!l.item?.value}
                                                    required
                                                />


                                            )
                                        }

                                    </>}
                                </ItemsGrid>

                            </Flex>
                        </Box>)
                    }
                </PageWrapper>
                <PageFooter>
                    <ButtonsContainer>
                        <CancelBtn />
                        <Button orange onClick={handleSubmit}>
                            Save
                        </Button>
                    </ButtonsContainer>
                </PageFooter>
            </Animation>
        </>
    )
}
